import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  CHANGE_CURRENT_STATE,
  ChangeCurrentStateAction,
  ChangeStatesFlowIfEmptyAction,
  CurrentStateChangedAction,
  FINISH_FIRST_STEPS,
  FinishFirstStepsAction,
  GO_TO_NEXT_APP_CONFIGURATION,
  GO_TO_NEXT_STATE,
  LOAD_INITIAL_STATE
} from './first-steps.actions';
import { getCurrentStateSelector, getStatesFlowSelector, State } from './first-steps.reducer';
import { filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { IState } from '../shared/models/state';
import { Router } from '@angular/router';
import { from } from 'rxjs';
import { defaultFirstStepsFlow, firstStateForMobile, firstStepsConfigurationSteps } from './first-steps.flow';
import { isMobile } from '../shared/utils/screen-width';

@Injectable()
export class FirstStepsEffects {
  @Effect()
  handleNextState = this.actions$.pipe(
    ofType(GO_TO_NEXT_STATE),
    withLatestFrom(this.store.select(getStatesFlowSelector), this.store.select(getCurrentStateSelector)),
    map(([, statesFlow, currentState]) => [statesFlow, currentState]),
    filter(([statesFlow]) => !!statesFlow && !!statesFlow.length),
    map(([statesFlow, currentState]: [State[], State]) => {
      if (!currentState) {
        return new ChangeCurrentStateAction(statesFlow[0]);
      }
      const currentStateIndex = statesFlow.indexOf(currentState);
      if (currentStateIndex === statesFlow.length - 1) {
        console.log('finish first steps')
        return new FinishFirstStepsAction();
      }
      return new ChangeCurrentStateAction(statesFlow[currentStateIndex + 1]);
    })
  );

  @Effect()
  goToNextAppConfiguration = this.actions$.pipe(
    ofType(GO_TO_NEXT_APP_CONFIGURATION),
    withLatestFrom(this.store.select(getStatesFlowSelector), this.store.select(getCurrentStateSelector)),
    map(([, statesFlow, currentState]) => [statesFlow, currentState]),
    filter(([statesFlow]) => !!statesFlow && !!statesFlow.length),
    map(([statesFlow, currentState]: [State[], State]) => {
      if (!currentState) {
        return new ChangeCurrentStateAction(statesFlow[0]);
      }
      const currentConfigurationStepIndex = firstStepsConfigurationSteps.findIndex(regex => regex.test(currentState));
      if (currentConfigurationStepIndex === firstStepsConfigurationSteps.length - 1) {
        return new FinishFirstStepsAction();
      }
      const nextState = statesFlow.find(state =>
        firstStepsConfigurationSteps[currentConfigurationStepIndex + 1].test(state)
      );
      return new ChangeCurrentStateAction(nextState);
    })
  );

  @Effect()
  changeCurrentState = this.actions$.pipe(
    ofType(CHANGE_CURRENT_STATE),
    map((action: ChangeCurrentStateAction) => action.payload),
    switchMap((state: State) => from(this.router.navigate(['first-steps', state]).then(() => state))),
    map(state => new CurrentStateChangedAction(state))
  );

  @Effect()
  loadInitialState = this.actions$.pipe(
    ofType(LOAD_INITIAL_STATE),
    withLatestFrom(this.store.select(getStatesFlowSelector), this.store.select(getCurrentStateSelector)),
    map(([, statesFlow, currentState]) => [statesFlow, currentState]),
    mergeMap(([statesFlow, currentState]: [State[], State]) => {
      const flow = statesFlow || defaultFirstStepsFlow;
      const stateToGo = currentState || (isMobile() ? firstStateForMobile(flow) : flow[0]);
      if (!statesFlow) {
        return [new ChangeStatesFlowIfEmptyAction(flow), new ChangeCurrentStateAction(stateToGo)];
      }
      return [new ChangeCurrentStateAction(stateToGo)];
    })
  );

  @Effect({ dispatch: false })
  finishFirstSteps = this.actions$.pipe(
    ofType(FINISH_FIRST_STEPS),
    tap(() => {
      this.router.navigate(['onboarding']);
    })
  );

  constructor(private actions$: Actions, private store: Store<IState>, private router: Router) {}
}
